// Block autocomplete.
//
// A single floating popover that offers prefix-matched suggestions
// based on the block the writer is editing.
//
//   character block      → past character cues (plus "(CONT'D)" when
//                          the same speaker resumes in the same scene)
//   scene_heading block  → past sluglines (location reuse), or, once
//                          " - " has been typed, the canonical
//                          time-of-day list (DAY / NIGHT / MORNING / …)
//
// Each suggester returns `{ matches, replaceFrom }`. `replaceFrom` is
// the offset in the block's current text where accepted text should
// start replacing — 0 means "replace the whole block" (character cues,
// full slugline reuse); a positive value preserves the prefix the
// writer already typed (time-of-day completion keeps the location
// half intact).
//
// Implemented as a PM Plugin with a `view` that owns the popover DOM
// node. The plugin's `props.handleKeyDown` intercepts keys while the
// popover is open so Tab doesn't fall through to the cycle command.

import { Plugin } from 'prosemirror-state';

const CHARACTER = 'character';
const SCENE_HEADING = 'scene_heading';
const CONTD_SUFFIX = " (CONT'D)";

// Industry-standard time-of-day terms. Order is preference order: the
// most common (DAY/NIGHT) lead so an empty prefix tab-completes to DAY.
const TIME_OF_DAY = [
  'DAY',
  'NIGHT',
  'MORNING',
  'EVENING',
  'AFTERNOON',
  'DUSK',
  'DAWN',
  'CONTINUOUS',
  'LATER',
  'MOMENTS LATER',
  'THE NEXT DAY',
  'SAME TIME',
];

// Match a slugline prefix: INT., EXT., INT/EXT., I/E. followed by a
// space. We use this to keep forced shot headings (".CLOSE ON …") out
// of the location reuse roster — they're rarely meant to be reused.
const SLUGLINE_PREFIX_RE = /^(INT\.|EXT\.|INT\/EXT\.|I\/E\.)\s/i;

function canonicalize(raw) {
  return raw.replace(/\s*\([^)]+\)\s*$/, '').toUpperCase();
}

// ── Character cue suggester ────────────────────────────────────────

function characterRoster(doc) {
  const seen = new Set();
  doc.forEach((node) => {
    if (node.type.name !== CHARACTER) return;
    const raw = (node.textContent || '').trim();
    if (!raw) return;
    const canonical = canonicalize(raw);
    if (canonical.length >= 2) seen.add(canonical);
  });
  return Array.from(seen).sort();
}

// Walk doc children backward from the current character block to find
// the most recent character cue in the same scene. Returns its canonical
// name, or null if a scene_heading is crossed first (new scene = no
// continuity) or no prior cue exists.
function previousSpokenInScene(state) {
  const $from = state.selection.$from;
  const myIndex = $from.index(0);
  const doc = state.doc;
  for (let i = myIndex - 1; i >= 0; i--) {
    const node = doc.child(i);
    const name = node.type.name;
    if (name === SCENE_HEADING) return null;
    if (name !== CHARACTER) continue;
    const raw = (node.textContent || '').trim();
    if (!raw) continue;
    return canonicalize(raw);
  }
  return null;
}

function characterSuggestions(state, blockNode) {
  const text = blockNode.textContent || '';
  const prefix = text.trim().toUpperCase();
  if (prefix.length === 0) return null;

  const matches = characterRoster(state.doc).filter(
    (name) => name !== prefix && name.startsWith(prefix),
  );

  // Pin "<NAME> (CONT'D)" to the top when the same speaker resumes
  // after an action break in this scene. The writer can still arrow
  // down to the bare name.
  const prev = previousSpokenInScene(state);
  if (prev && prev.startsWith(prefix)) {
    const contd = `${prev}${CONTD_SUFFIX}`;
    const idx = matches.indexOf(contd);
    if (idx !== -1) matches.splice(idx, 1);
    matches.unshift(contd);
  }

  if (matches.length === 0) return null;
  return { matches, replaceFrom: 0 };
}

// ── Scene heading suggester ────────────────────────────────────────

function sluglineRoster(doc) {
  const seen = new Set();
  doc.forEach((node) => {
    if (node.type.name !== SCENE_HEADING) return;
    const raw = (node.textContent || '').trim();
    if (!SLUGLINE_PREFIX_RE.test(raw)) return; // skip forced shot headings
    seen.add(raw.toUpperCase());
  });
  return Array.from(seen).sort();
}

function sceneHeadingSuggestions(state, blockNode) {
  const text = blockNode.textContent || '';

  // Stage 2: once " - " is present, complete the time-of-day tail. We
  // only replace text AFTER the last " - " — the location part the
  // writer typed stays put.
  const dashIdx = text.lastIndexOf(' - ');
  if (dashIdx !== -1) {
    const todPrefix = text.slice(dashIdx + 3).toUpperCase();
    const matches = TIME_OF_DAY.filter((t) => t !== todPrefix && t.startsWith(todPrefix));
    if (matches.length === 0) return null;
    return { matches, replaceFrom: dashIdx + 3 };
  }

  // Stage 1: location reuse. Suggest past sluglines whose uppercased
  // text prefix-matches what's been typed so far.
  const prefix = text.trim().toUpperCase();
  if (prefix.length === 0) return null;
  const matches = sluglineRoster(state.doc).filter((h) => h !== prefix && h.startsWith(prefix));
  if (matches.length === 0) return null;
  return { matches, replaceFrom: 0 };
}

// ── Dispatcher ─────────────────────────────────────────────────────

function suggestionsForCursor(state) {
  const $from = state.selection.$from;
  if ($from.depth < 1) return null;
  const node = $from.node(1);
  if (node.type.name === CHARACTER) return characterSuggestions(state, node);
  if (node.type.name === SCENE_HEADING) return sceneHeadingSuggestions(state, node);
  return null;
}

// ── Plugin view ────────────────────────────────────────────────────

function rectAfterCursor(view) {
  const $from = view.state.selection.$from;
  if ($from.depth < 1) return null;
  const blockPos = $from.before(1);
  let dom;
  try {
    dom = view.nodeDOM(blockPos);
  } catch {
    return null;
  }
  if (!dom || !dom.getBoundingClientRect) return null;
  return dom.getBoundingClientRect();
}

class AutocompleteView {
  constructor(view) {
    this.view = view;
    this.open = false;
    this.matches = [];
    this.activeIdx = 0;
    this.replaceFrom = 0;
    this.el = document.createElement('div');
    this.el.className = 'pm-autocomplete';
    this.el.style.display = 'none';
    document.body.appendChild(this.el);
    this.onClick = this.onClick.bind(this);
    this.onScrollOrResize = this.onScrollOrResize.bind(this);
    this.el.addEventListener('mousedown', this.onClick);
    // Body-attached + fixed positioning means scroll/resize don't drag
    // the popover with the block — re-anchor on both (capture phase so
    // nested scroll containers still notify us).
    window.addEventListener('scroll', this.onScrollOrResize, true);
    window.addEventListener('resize', this.onScrollOrResize);
    this.update(view);
  }

  onScrollOrResize() {
    if (!this.open) return;
    this.position();
  }

  onClick(e) {
    const item = e.target.closest('[data-match]');
    if (!item) return;
    e.preventDefault(); // mousedown — beat blur, keep editor focus
    this.accept(item.dataset.match);
  }

  accept(value) {
    if (!value) return;
    const view = this.view;
    const $from = view.state.selection.$from;
    if ($from.depth < 1) return;
    const before = $from.before(1);
    const after = $from.after(1);
    const cur = $from.node(1);
    // Preserve the writer's prefix up to `replaceFrom`, then splice in
    // the chosen suggestion. For full-block replace (character cue,
    // slugline reuse) replaceFrom is 0 so this collapses to "swap the
    // whole text."
    const keep = (cur.textContent || '').slice(0, this.replaceFrom);
    const nextText = keep + value;
    const tr = view.state.tr.replaceWith(
      before,
      after,
      cur.type.create(cur.attrs, view.state.schema.text(nextText)),
    );
    const Sel = view.state.selection.constructor;
    tr.setSelection(Sel.create(tr.doc, before + 1 + nextText.length));
    view.dispatch(tr);
    view.focus();
    this.hide();
  }

  hide() {
    if (!this.open) return;
    this.open = false;
    this.el.style.display = 'none';
  }

  render(matches, activeIdx) {
    this.matches = matches;
    this.activeIdx = activeIdx;
    this.el.innerHTML = matches
      .map(
        (m, i) =>
          `<div class="pm-autocomplete-item${i === activeIdx ? ' active' : ''}" data-match="${m}">${m}</div>`,
      )
      .join('');
  }

  position() {
    const rect = rectAfterCursor(this.view);
    if (!rect) {
      this.hide();
      return;
    }
    const vh = window.innerHeight;
    if (rect.bottom < 0 || rect.top > vh) {
      this.hide();
      return;
    }
    this.el.style.top = `${rect.bottom + window.scrollY + 4}px`;
    this.el.style.left = `${rect.left + window.scrollX}px`;
  }

  update(view) {
    this.view = view;
    const result = suggestionsForCursor(view.state);
    if (!result) {
      this.hide();
      return;
    }
    this.replaceFrom = result.replaceFrom;
    if (!this.open) this.activeIdx = 0;
    else if (this.activeIdx >= result.matches.length) this.activeIdx = 0;
    this.open = true;
    this.el.style.display = 'block';
    this.render(result.matches, this.activeIdx);
    this.position();
  }

  destroy() {
    this.el.removeEventListener('mousedown', this.onClick);
    window.removeEventListener('scroll', this.onScrollOrResize, true);
    window.removeEventListener('resize', this.onScrollOrResize);
    if (this.el.parentNode) this.el.parentNode.removeChild(this.el);
  }
}

export function autocompletePlugin() {
  let acView = null;
  return new Plugin({
    view(editorView) {
      acView = new AutocompleteView(editorView);
      return {
        update: (view) => acView.update(view),
        destroy: () => {
          acView.destroy();
          acView = null;
        },
      };
    },
    props: {
      handleKeyDown(_view, event) {
        if (!acView || !acView.open) return false;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            acView.activeIdx = (acView.activeIdx + 1) % acView.matches.length;
            acView.render(acView.matches, acView.activeIdx);
            return true;
          case 'ArrowUp':
            event.preventDefault();
            acView.activeIdx =
              (acView.activeIdx - 1 + acView.matches.length) % acView.matches.length;
            acView.render(acView.matches, acView.activeIdx);
            return true;
          case 'Tab': {
            // Tab = explicit accept (matches Final Draft / Highland).
            // Block-type cycle is reachable only on empty blocks not
            // matched by any suggester, so Tab here is unambiguous.
            const pick = acView.matches[acView.activeIdx];
            if (!pick) return false;
            event.preventDefault();
            acView.accept(pick);
            return true;
          }
          case 'Enter':
            // Falls through so the editor keymap can move the writer on
            // to the next block type (character → dialogue, etc.).
            acView.hide();
            return false;
          case 'Escape':
            event.preventDefault();
            acView.hide();
            return true;
          default:
            return false;
        }
      },
    },
  });
}
